import { newFieldId } from './fieldIds';

// The field-type registry (build plan §5.4). One source of truth for the
// palette, canvas, properties, preview, submissions formatting and client
// validation. Data only — no React. The defaults and LIMITS mirror the
// server's shared/form-builder/field-types.js exactly (plan §2.3).

const BASIC_HELP =
  "Everyday questions: short and long text, a person's name, a postal address, email, phone, numbers, and choices (dropdown, radio buttons and checkboxes). Most forms are built entirely from these. Name, Address, Email and Phone check what people type before they can submit.";
const ADVANCED_HELP =
  "Dates, file uploads, tables, repeating lists (for example one row per crew member), matrix grids and star ratings, plus content blocks that aren't questions: a description area for instructions, a logo or image, and a YouTube or Vimeo video.";
const LAYOUT_HELP =
  'Sections split a long form into titled groups. Every field after a section belongs to it until the next section. A section can have a description and a wide header image. In the app, people see the section title above its fields.';

export const CATEGORIES = [
  { key: 'basic', label: 'Basic Fields', help: BASIC_HELP },
  { key: 'advanced', label: 'Advanced Fields', help: ADVANCED_HELP },
  { key: 'layout', label: 'Layout', help: LAYOUT_HELP },
];

const type = (key, label, category, icon, layout, hasPlaceholder) => ({
  key,
  label,
  category,
  icon,
  layout,
  hasPlaceholder,
});

export const FIELD_TYPES = {
  short_answer: type(
    'short_answer',
    'Short Answer',
    'basic',
    'type',
    false,
    true
  ),
  long_answer: type(
    'long_answer',
    'Long Answer',
    'basic',
    'long-answer',
    false,
    true
  ),
  name: type('name', 'Name', 'basic', 'user', false, false),
  address: type('address', 'Address', 'basic', 'map-pin', false, false),
  email: type('email', 'Email Address', 'basic', 'mail', false, true),
  phone: type('phone', 'Phone Number', 'basic', 'phone', false, false),
  number: type('number', 'Number', 'basic', 'hash', false, true),
  dropdown: type(
    'dropdown',
    'Dropdown List',
    'basic',
    'chevron-down',
    false,
    true
  ),
  radio: type('radio', 'Radio Button', 'basic', 'radio-button', false, false),
  checkbox: type('checkbox', 'Checkbox', 'basic', 'check-square', false, false),
  datetime: type(
    'datetime',
    'Date / Time',
    'advanced',
    'calendar',
    false,
    true
  ),
  file_upload: type(
    'file_upload',
    'File Upload',
    'advanced',
    'upload',
    false,
    false
  ),
  table: type('table', 'Table', 'advanced', 'table', false, false),
  repeater: type(
    'repeater',
    'Repeating List',
    'advanced',
    'list-plus',
    false,
    false
  ),
  matrix: type('matrix', 'Matrix', 'advanced', 'layout-grid', false, false),
  rating: type('rating', 'Rating', 'advanced', 'star', false, false),
  description: type(
    'description',
    'Description Area',
    'advanced',
    'text',
    true,
    false
  ),
  image: type('image', 'Logo / Image', 'advanced', 'image', true, false),
  video: type('video', 'Video', 'advanced', 'video', true, false),
  section: type('section', 'Section', 'layout', 'menu', true, false),
  // System types (w9-plan §2.3): only a `kind = 'w9'` definition carries
  // them. They are never offered by the palette or the saved-field library;
  // the server refuses them in a custom form.
  tin: {
    ...type('tin', 'Taxpayer ID (TIN)', 'system', 'hash', false, false),
    system: true,
  },
  signature: {
    ...type('signature', 'Signature', 'system', 'pen', false, false),
    system: true,
  },
  tax_class: {
    ...type('tax_class', 'Tax Classification', 'system', 'list', false, false),
    system: true,
  },
};

// The system types, in registry order (w9-plan §2.3 SYSTEM_TYPES).
export const SYSTEM_TYPES = ['tin', 'signature', 'tax_class'];

export const isSystemType = (fieldType) =>
  !!FIELD_TYPES[fieldType] && FIELD_TYPES[fieldType].system === true;

// Palette entries that aren't a type of their own: they add a field of
// `base` with preset settings. An Agreement is a checkbox with
// config.single (one "I agree" box under long-form copy in options[0]), so
// the server, the web form and the app need nothing new.
export const PALETTE_PRESETS = {
  agreement: {
    key: 'agreement',
    label: 'Agreement',
    category: 'basic',
    icon: 'shield-check',
    base: 'checkbox',
  },
};

// The extra choice a dropdown or radio shows when config.allow_other is on;
// picking it reveals a text box and the typed text is the answer.
export const OTHER_LABEL = 'Other';

// The checkbox text an agreement shows when agree_label is blank.
export const AGREE_LABEL_DEFAULT = 'I have read and agree';
export const AGREEMENT_TEXT_DEFAULT =
  'Paste the full agreement here: waiver, release, rules or terms. People check the box below to agree.';

const PRESET_DEFAULTS = {
  agreement: {
    label: 'Terms & Conditions',
    required: true,
    config: {
      options: [AGREEMENT_TEXT_DEFAULT],
      single: true,
      agree_label: AGREE_LABEL_DEFAULT,
    },
  },
};

// Palette metadata ({ key, label, icon, category }) for a type or a preset.
export const paletteEntry = (key) =>
  FIELD_TYPES[key] || PALETTE_PRESETS[key] || null;

export const PALETTE_ORDER = {
  basic: [
    'short_answer',
    'long_answer',
    'name',
    'address',
    'email',
    'phone',
    'number',
    'dropdown',
    'radio',
    'checkbox',
    'agreement',
  ],
  advanced: [
    'datetime',
    'file_upload',
    'table',
    'repeater',
    'matrix',
    'rating',
    'description',
    'image',
    'video',
  ],
  layout: ['section'],
};

export const LAYOUT_TYPES = ['section', 'description', 'image', 'video'];

export const isLayoutType = (fieldType) => LAYOUT_TYPES.includes(fieldType);

// Per-type defaults (plan §2.3): everything except `id` and `type`.
// `config` holds the per-type keys in their canonical order.
const DEFAULTS = {
  short_answer: { label: 'Short Answer', required: false, config: {} },
  long_answer: { label: 'Long Answer', required: false, config: {} },
  name: { label: 'Name', required: true, config: { middle: false } },
  address: { label: 'Address', required: true, config: {} },
  email: {
    label: 'Email Address',
    placeholder: 'name@example.com',
    required: true,
    config: {},
  },
  phone: { label: 'Phone Number', required: true, config: {} },
  number: { label: 'Number', required: false, config: {} },
  dropdown: {
    label: 'Dropdown List',
    placeholder: 'Select an option',
    required: false,
    config: { options: ['Option 1', 'Option 2', 'Option 3'] },
  },
  radio: {
    label: 'Choose one',
    required: false,
    config: { options: ['Option 1', 'Option 2', 'Option 3'] },
  },
  checkbox: {
    label: 'Select all that apply',
    required: false,
    config: { options: ['Option 1', 'Option 2'], single: false },
  },
  datetime: {
    label: 'Date',
    placeholder: 'MM / DD / YYYY',
    required: false,
    config: {},
  },
  file_upload: {
    label: 'Upload a file',
    help: 'JPG, PNG or PDF · max 10 MB',
    required: false,
    config: {},
  },
  table: {
    label: 'Table',
    required: false,
    config: { columns: ['Item', 'Qty'], rows: 2 },
  },
  repeater: {
    label: 'Crew members',
    help: 'Add everyone who needs a pit credential',
    required: false,
    config: { columns: ['Name', 'Role'], add_label: 'Add another', max: 10 },
  },
  matrix: {
    label: 'Rate the following',
    required: false,
    config: { rows: ['Track', 'Staff'], cols: ['Poor', 'OK', 'Great'] },
  },
  rating: { label: 'Rating', required: false, config: { max: 5 } },
  section: {
    label: 'Section Title',
    required: false,
    config: { banner: false, banner_asset_id: null },
  },
  description: {
    label: 'Description',
    required: false,
    config: { text: 'Add instructions or context…' },
  },
  image: {
    label: 'Logo',
    required: false,
    config: { size: 'medium', align: 'center', asset_id: null },
  },
  video: { label: 'Video', required: false, config: { url: '', caption: '' } },
  tin: { label: 'Taxpayer Identification Number', required: true, config: {} },
  signature: {
    label: 'Signature',
    required: true,
    config: { modes: ['typed', 'drawn'] },
  },
  tax_class: {
    label: 'Federal tax classification',
    required: true,
    config: {},
  },
};

// Signature modes a `signature` field's config.modes may hold.
export const SIGNATURE_MODES = ['typed', 'drawn'];

const cloneConfig = (config) => {
  const out = {};
  Object.keys(config).forEach((k) => {
    const v = config[k];
    out[k] = Array.isArray(v) ? v.slice() : v;
  });
  return out;
};

// A new field of `fieldType` with the plan's defaults (null for an unknown
// type, as on the server). Keys always come out in the canonical order: id,
// type, label, help, placeholder, required, map, acct, config.
export const makeField = (fieldType, id = newFieldId()) => {
  const d = DEFAULTS[fieldType];
  if (!d) return null;
  return {
    id,
    type: fieldType,
    label: d.label,
    help: d.help || '',
    placeholder: d.placeholder || '',
    required: d.required,
    map: null,
    acct: false,
    config: cloneConfig(d.config),
  };
};

// A new field for a palette entry: a preset (Agreement) is its base type
// with the preset's settings; anything else is makeField.
export const makePaletteField = (key, id = newFieldId()) => {
  const preset = PALETTE_PRESETS[key];
  if (!preset) return makeField(key, id);
  const base = makeField(preset.base, id);
  const d = PRESET_DEFAULTS[key];
  return {
    ...base,
    label: d.label,
    required: d.required,
    config: { ...base.config, ...cloneConfig(d.config) },
  };
};

// Palette name of a type ("Dropdown List"); falls back to the raw key.
export const typeLabel = (fieldType) =>
  FIELD_TYPES[fieldType] ? FIELD_TYPES[fieldType].label : fieldType;

// A checkbox with config.single is an Agreement everywhere in the builder.
export const isAgreement = (field) =>
  !!field &&
  field.type === 'checkbox' &&
  !!field.config &&
  field.config.single === true;

// The type name and icon a field shows (canvas tag, settings header).
export const fieldTypeLabel = (field) =>
  isAgreement(field)
    ? PALETTE_PRESETS.agreement.label
    : typeLabel(field && field.type);

export const fieldTypeIcon = (field) => {
  if (isAgreement(field)) return PALETTE_PRESETS.agreement.icon;
  const meta = field && FIELD_TYPES[field.type];
  return meta ? meta.icon : 'type';
};

// Plan §2.3 limits. Key names and values are identical to the server's
// LIMITS in shared/form-builder/field-types.js (chosen by G1), so client and
// server validation agree. Text limits are characters.
export const LIMITS = {
  fields: 200,
  label: 200,
  help: 1000,
  placeholder: 200,
  optionText: 200,
  // An agreement's text (options[0] of a single checkbox) and its checkbox
  // text (config.agree_label).
  agreementText: 20000,
  agreeLabel: 200,
  options: 100,
  columnsMin: 1,
  columnsMax: 10,
  tableRowsMin: 1,
  tableRowsMax: 20,
  repeaterMaxMin: 1,
  repeaterMaxMax: 50,
  addLabel: 50,
  matrixRowsMin: 1,
  matrixRowsMax: 30,
  matrixColsMin: 2,
  matrixColsMax: 10,
  ratingMaxMin: 3,
  ratingMaxMax: 10,
  descriptionText: 5000,
  videoUrl: 500,
  videoCaption: 500,
  formName: 200,
  formDescription: 5000,
  recipients: 25,
  shortAnswer: 500,
  cell: 500,
  longAnswer: 5000,
  namePart: 200,
  addressPart: 200,
  answersBytes: 262144,
};

// How many fields collect an answer (known types that aren't layout
// blocks). Takes a fields array or a definition ({ fields }), like the
// server's countAnswerFields.
export const countAnswerFields = (fields) => {
  const list = Array.isArray(fields) ? fields : (fields && fields.fields) || [];
  return list.filter((f) => f && FIELD_TYPES[f.type] && !isLayoutType(f.type))
    .length;
};
